import axios from "axios";
import { refreshAccessToken } from "../utils/refreshAccessToken";
import { emitSessionExpired } from "../utils/sessionEvents";
import { isJwtExpired } from "../utils/jwtUtils";
import { API_ROUTES } from "../routes";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_INTERNAL_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Public auth endpoints — 401 here means "wrong credentials" / "invalid challenge",
// NOT "session expired". Bubble the error up to the caller (LoginBox, ForgotPasswordFlow)
// so it can show an inline error instead of the SessionExpiredModal overlay.
const PUBLIC_AUTH_ENDPOINTS: readonly string[] = [
  API_ROUTES.LOGIN,
  API_ROUTES.RESPOND_CHALLENGE,
  API_ROUTES.FORGOT_PASSWORD,
  API_ROUTES.CONFIRM_FORGOT_PASSWORD,
];

const isPublicAuthEndpoint = (url: string | undefined): boolean => {
  if (!url) return false;
  return PUBLIC_AUTH_ENDPOINTS.some((path) => url.includes(path));
};

let isRedirecting = false;

// Add response interceptor for 401 handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalConfig = error.config;

    // [AUTH] 401 Unauthorized — attempt token refresh before logging out
    if (status === 401) {
      // Public auth endpoints (login, forgot-password, respond-challenge, confirm-forgot-password)
      // return 401 for invalid credentials or invalid Cognito challenge sessions, not for an
      // expired user session. Skip refresh + session-expired modal and let the caller surface
      // the error inline.
      if (isPublicAuthEndpoint(originalConfig?.url)) {
        console.warn(
          "[AUTH][datasurfr][api/index.ts] 401 from public auth endpoint | url:",
          originalConfig?.url,
          "— bubbling up (invalid credentials, not session expiry)"
        );
        return Promise.reject(error);
      }

      // [AUTH] prevent infinite retry loop: only retry once per request
      if (originalConfig._retried) {
        console.warn("[AUTH][datasurfr][api/index.ts] 401 after retry — refresh token also expired | url:", originalConfig.url);

        if (isRedirecting) {
          console.warn("[AUTH][datasurfr][api/index.ts] Already redirecting — duplicate suppressed");
          return Promise.reject(error);
        }
        isRedirecting = true;

        // [AUTH] both tokens expired — clear session and show modal
        console.warn("[AUTH][datasurfr][api/index.ts] Clearing localStorage + sessionStorage");
        localStorage.clear();
        sessionStorage.clear();

        setTimeout(() => {
          isRedirecting = false;
          console.log("[AUTH][datasurfr][api/index.ts] isRedirecting flag reset");
        }, 5000);

        console.warn("[AUTH][datasurfr][api/index.ts] Emitting session-expired | reason: both tokens expired");
        emitSessionExpired("expired");
        return Promise.reject(error);
      }

      console.warn("[AUTH][datasurfr][api/index.ts] 401 received | url:", originalConfig.url, "— attempting token refresh before logout");

      // [AUTH] try to get a new access token using the refresh token.
      // refreshAccessToken() returns null for fatal expiry (logout), throws for transient errors (keep session).
      let newToken: string | null = null;
      try {
        newToken = await refreshAccessToken();
      } catch {
        // Refresh endpoint returned 5xx or network error.
        // If the access token is also expired locally, the user cannot recover —
        // show the session-expired modal. Otherwise it's a true transient failure.
        const authHeader = originalConfig.headers?.Authorization as string | undefined;
        const bearerToken = authHeader?.replace("Bearer ", "");
        const tokenExpiredLocally = bearerToken ? isJwtExpired(bearerToken) : false;

        if (tokenExpiredLocally) {
          console.warn("[AUTH][datasurfr][api/index.ts] Refresh failed (5xx/network) AND access token is locally expired — emitting session-expired | url:", originalConfig.url);
          if (!isRedirecting) {
            isRedirecting = true;
            setTimeout(() => { isRedirecting = false; }, 5000);
            localStorage.clear();
            sessionStorage.clear();
            emitSessionExpired("expired");
          }
        } else {
          console.warn("[AUTH][datasurfr][api/index.ts] Refresh failed with transient error — access token still valid, keeping session alive | url:", originalConfig.url);
        }
        return Promise.reject(error);
      }

      if (newToken) {
        // [AUTH] refresh succeeded — retry original request with new token
        console.log("[AUTH][datasurfr][api/index.ts] Token refreshed — retrying original request | url:", originalConfig.url);
        originalConfig._retried = true;
        originalConfig.headers = {
          ...originalConfig.headers,
          Authorization: `Bearer ${newToken}`,
        };
        return api(originalConfig);
      }

      // [AUTH] refresh returned null — refresh token is also expired, show modal
      console.warn("[AUTH][datasurfr][api/index.ts] Token refresh failed (refresh token expired) | url:", originalConfig.url, "— showing session-expired modal");

      if (isRedirecting) {
        console.warn("[AUTH][datasurfr][api/index.ts] Already redirecting — duplicate suppressed");
        return Promise.reject(error);
      }
      isRedirecting = true;

      console.warn("[AUTH][datasurfr][api/index.ts] Clearing localStorage + sessionStorage");
      localStorage.clear();
      sessionStorage.clear();

      setTimeout(() => {
        isRedirecting = false;
        console.log("[AUTH][datasurfr][api/index.ts] isRedirecting flag reset");
      }, 5000);

      console.warn("[AUTH][datasurfr][api/index.ts] Emitting session-expired | reason: refresh token expired");
      emitSessionExpired("expired");
      return Promise.reject(error);
    }

    // [AUTH] 403 Forbidden — show permission error, do NOT logout (session is still valid)
    if (status === 403) {
      console.warn("[AUTH][datasurfr][api/index.ts] 403 Forbidden | url:", originalConfig?.url, "— session valid, permission denied");
    }

    // [AUTH] 5xx Server Error — server issue, do NOT logout (session is still valid)
    if (status !== undefined && status >= 500) {
      console.warn(`[AUTH][datasurfr][api/index.ts] ${status} Server Error | url:`, originalConfig?.url, "— server issue, no logout");
    }

    return Promise.reject(error);
  }
);

export default api;

export const post = (url: string, data: any, config = {}) => {
  return api.post(url, data, {
    withCredentials: true,
    ...config,
  });
};
